/**
 * "Naming her boards — bigger than it looks."
 *
 * The rule: ONE board keeps the name ("Don't lose Mom's board") because naming
 * her is what makes the loss real. TWO OR MORE drops the name and shows the
 * people instead — 🌷 Mom · ⛳ Dad · 🎀 Emma · +1 — truncated at three, newest
 * first.
 *
 * The spec is explicit that this is not a copy change: everything that asks
 * about her boards in aggregate currently assumes there is exactly one, and the
 * assumption reaches into the capture callable's contract (it sends a single
 * `recipientName` + `boardUrl`, so the EMAIL physically cannot do the 1/2/3+
 * subject rule). That half is Manny's — a shared-schema change, additive only.
 * This module is the front-end half, and it is deliberately a pure function of
 * the list so the sheet, the You card, the claim page and (later) the email
 * body can never render three different answers.
 *
 * Every helper takes the LIST, never a name. Passing one name is exactly the
 * bug: the three callers on master (the top-right door, the account menu, the
 * You page) all pass the most-recent recipient, which is why the headline can
 * only ever name one person.
 */

import type { BoardSummary } from './accountState';

/** How many people show before we collapse to "+N". */
export const MAX_VISIBLE_BOARDS = 3;

export interface BoardListDisplay {
  /** Newest first, capped at MAX_VISIBLE_BOARDS. */
  visible: BoardSummary[];
  /** How many are hidden behind the "+N". 0 when nothing is hidden. */
  overflow: number;
}

/**
 * Newest first, truncated at three plus a count. Non-mutating: the caller's
 * array order is the source of truth for "newest", so this never sorts — it
 * would need a timestamp it is not given, and silently re-ordering would be
 * worse than trusting the caller (recipients arrive newest-first already).
 */
export function boardListDisplay(boards: BoardSummary[]): BoardListDisplay {
  const visible = boards.slice(0, MAX_VISIBLE_BOARDS);
  return { visible, overflow: Math.max(0, boards.length - visible.length) };
}

const RSQUO = '’';

/**
 * The capture sheet's headline. One board names her; two or more does not.
 *
 * Zero boards is reachable from the account surfaces (the dropdown row and the
 * You card can be opened without a board in context) and used to render
 * "Don't lose your's board" — the plural is the correct fallback, not a name.
 */
export function captureHeadline(boards: BoardSummary[]): string {
  if (boards.length === 1) return `Don${RSQUO}t lose ${boards[0].name}${RSQUO}s board`;
  return `Don${RSQUO}t lose your boards`;
}

/**
 * The claim page's headline.
 *
 * "The word *still* is doing the work — it names the worry (I thought I'd lost
 * that) and resolves it in the same breath, and it scales across both cases
 * instead of needing two unrelated headlines."
 */
export function claimHeadline(boards: BoardSummary[]): string {
  if (boards.length === 1) return `${boards[0].name}${RSQUO}s board is still here`;
  return `It${RSQUO}s all still here`;
}

/**
 * "12 ideas" under the thumbnails. Pluralised here so the three surfaces that
 * show it cannot disagree.
 */
export function ideaCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'idea' : 'ideas'}`;
}

/**
 * The TAP-opened sign-in pop-up's headline (THE-975 / THE-967 PRD "3. The
 * sign-in pop-up"). Deliberately a SEPARATE function from `captureHeadline`
 * above, not a new branch inside it: the timed prompt (THE-962, in flight)
 * owns `captureHeadline` and must keep seeing exactly today's wording —
 * `V4SignInModal`'s `variant` prop is what decides which of the two a given
 * caller gets, so the same `boards` list can never be reinterpreted by both.
 *
 * Call only with `boards.length > 0` — the no-boards case is NO CHANGE
 * (today's modal already falls back to 'Log in'/'Create account') and is
 * handled by the caller, not here.
 *
 * Distinguishes her OWN board (`isMe`) by name, which `captureHeadline` never
 * had occasion to: "your board" instead of her own recipient name.
 */
export function tapCaptureHeadline(boards: BoardSummary[]): string {
  if (boards.length === 1) {
    const [board] = boards;
    return board.isMe
      ? `Don${RSQUO}t lose your board`
      : `Don${RSQUO}t lose ${board.name}${RSQUO}s board`;
  }
  if (boards.length === 2) {
    const mine = boards.find((b) => b.isMe);
    const other = boards.find((b) => !b.isMe);
    if (mine && other) return `Don${RSQUO}t lose your board and ${other.name}${RSQUO}s`;
    const [a, b] = boards;
    return `Don${RSQUO}t lose ${a.name}${RSQUO}s and ${b.name}${RSQUO}s boards`;
  }
  return `Don${RSQUO}t lose your ${boards.length} boards`;
}

/**
 * The line under the tap headline, singular for one board and plural for
 * more ("it'll" vs "they'll") — the only part of the sentence that varies.
 */
export function tapCaptureLine(boardCount: number): string {
  return boardCount === 1
    ? `Make an account and it${RSQUO}ll be here next time, on any device.`
    : `Make an account and they${RSQUO}ll be here next time, on any device.`;
}
